<template>
  <v-container
    class="align-content-start pa-0"
    fill-height
    fluid
  >
    <!-- Loading State -->
    <v-row v-if="!FindBankRegressionData">
      <v-col
        cols="12"
        class="text-center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <div class="mt-4 text-h6">
          Loading rate peers data...
        </div>
      </v-col>
    </v-row>

    <!-- Data Display -->
    <template v-else-if="FindBankRegressionData">
      <v-row>
        <v-col
          cols="12"
          lg="5"
        >
          <repricing-speed
            :data="repricingSpeedData"
            :highlighted-bank="highlightedBankName"
          />
        </v-col>
        <v-col
          cols="12"
          lg="7"
        >
          <franchise-strength
            :data="franchiseStrengthData"
            :highlighted-bank="highlightedBankName"
          />
        </v-col>
      </v-row>

      <!-- Margin Spread and Peers NII Sensitivity Cards -->
      <v-row class="mt-4">
        <v-col
          cols="12"
          lg="7"
        >
          <margin-spread
            :data="marginSpreadData"
            :highlighted-bank="highlightedBankName"
          />
        </v-col>
        <v-col
          cols="12"
          lg="5"
        >
          <peers-nii-sensitivity
            :asset-label="assetLabel"
            :data="niiSensitivityData"
            :highlighted-bank="highlightedBankName"
          />
        </v-col>
      </v-row>
    </template>

    <!-- No Data State -->
    <v-row v-else>
      <v-col
        cols="12"
        class="text-center"
      >
        <v-alert
          type="info"
          prominent
          text
        >
          <div class="text-h6">
            No rates data available
          </div>
          <div class="mt-2">
            Please check your account configuration or try again later.
          </div>
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RepricingSpeed from './blocks/peers/RepricingSpeed.vue'
import FranchiseStrength from './blocks/peers/FranchiseStrength.vue'
import MarginSpread from './blocks/peers/MarginSpread.vue'
import PeersNIISensitivity from './blocks/peers/PeersNIISensitivity.vue'

export default {
  name      : 'RatesPeers',
  components: {
    RepricingSpeed,
    FranchiseStrength,
    MarginSpread,
    'peers-nii-sensitivity': PeersNIISensitivity
  },
  dataStore: {
    FindBankRegressionData: 'Rates.FindBankRegressionData.Data'
  },
  props: {
    breakpoint: {
      type   : Number,
      default: 6
    },
    bankRegressionData: {
      type   : Object,
      default: null
    }
  },
  data () {
    return {}
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },
    // Get data from the dataStore (same as Rates.vue)
    bankRegressionDataFromStore () {
      return this.FindBankRegressionData || null
    },
    // Direct access to peers data like existing blocks
    repricingSpeedData () {
      const data = this.FindBankRegressionData?.peers?.repricingSpeed?.peersData || []
      return data.map(peer => ({
        bankName        : peer.peerName || 'Unknown Bank',
        depositSpeed    : peer.depositSpeed || 0,
        depositLoanRatio: peer.depositLoanSpeedRatio || 0,
        isBank          : peer.bank || false,
        loanSpeed       : peer.loanSpeed || 0
      }))
    },
    franchiseStrengthData () {
      const data = this.FindBankRegressionData?.peers?.franchiseStrength?.perPeerFranchiseStrength || []
      return data.map(peer => ({
        bankName: peer.peerName || 'Unknown Bank',
        value   : peer.franchiseStrength || 0,
        isBank  : peer.bank || false
      }))
    },
    marginSpreadData () {
      const data = this.FindBankRegressionData?.peers?.marginSpread?.perPeerMarginSpread || []
      return data.map(peer => ({
        bankName: peer.peerName || 'Unknown Bank',
        value   : peer.marginSpread || 0,
        isBank  : peer.bank || false
      }))
    },
    niiSensitivityData () {
      const data = this.FindBankRegressionData?.peers?.niiSensitivity?.perPeerNIISensitivity || []
      return data.map(peer => ({
        bankName: peer.peerName || 'Unknown Bank',
        sofr    : peer.sofrFactor || 0,
        slope   : peer.slopeFactor || 0,
        isBank  : peer.bank || false
      }))
    },
    assetLabel () {
      return this.FindBankRegressionData?.peers?.niiSensitivity?.assetLabel || ''
    },
    highlightedBankName () {
      if (!this.FindBankRegressionData?.peers) return 'CTBC Bank'

      // Try to find the bank in any of the peer data
      const repricingData = this.FindBankRegressionData?.peers?.repricingSpeed?.peersData || []
      const bankPeer = repricingData.find(peer => peer.bank)

      return bankPeer?.peerName || 'CTBC Bank'
    }
  }

}
</script>

<style scoped>
</style>
